import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CaretLeft, CaretRight, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { hm, ukTime, addDays, isoDay, ukDate } from "./hrUtils";

const DAY = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** Week view of clock entries: per-day bars against contracted, entry list; Ops Directors can correct entries. */
export const WeekTimesheet = ({ userId, canEdit, refreshKey }) => {
  const monday = () => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return isoDay(d); };
  const [week, setWeek] = useState(monday());
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await API.get(`/hr/clock?week=${week}${userId ? `&user_id=${userId}` : ""}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [week, userId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const perDay = data?.contracted_minutes ? data.contracted_minutes / 5 : 0;
  const maxBar = Math.max(perDay || 0, ...(data?.days || []).map((d) => d.minutes), 60);

  const save = async () => {
    try {
      await API.put(`/hr/clock/${edit.id}`, { clock_in: edit.clock_in ? new Date(edit.clock_in).toISOString() : undefined, clock_out: edit.clock_out ? new Date(edit.clock_out).toISOString() : undefined, note: edit.note });
      toast.success("Entry updated"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this clock entry?")) return;
    try { await API.delete(`/hr/clock/${id}`); toast.success("Entry removed"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="hr-week-timesheet">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setWeek(addDays(week, -7))} data-testid="hr-week-prev"><CaretLeft /></Button>
        <div className="text-center">
          <p className="text-sm font-semibold">Week of {ukDate(week)}</p>
          <p className="text-xs text-muted-foreground tabular-nums">{hm(data?.total_minutes)} worked · {hm(data?.contracted_minutes)} contracted</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setWeek(addDays(week, 7))} disabled={week >= monday()} data-testid="hr-week-next"><CaretRight /></Button>
      </div>
      <div className="grid grid-cols-7 gap-2 items-end h-28">
        {(data?.days || []).map((d, i) => {
          const h = Math.round((d.minutes / maxBar) * 100);
          const over = perDay && d.minutes > perDay;
          return (
            <div key={d.date} className="flex flex-col items-center justify-end h-full gap-1" title={`${ukDate(d.date)}: ${hm(d.minutes)}`}>
              <span className="text-[10px] tabular-nums text-muted-foreground">{d.minutes ? hm(d.minutes) : ""}</span>
              <div className="w-full rounded-t-md bg-muted relative" style={{ height: `${Math.max(h, 3)}%` }}>
                <div className={`absolute inset-0 rounded-t-md ${over ? "bg-amber-500" : i >= 5 ? "bg-slate-400" : "bg-primary"}`} />
              </div>
              <span className="text-[11px] font-medium">{DAY[i]}</span>
            </div>
          );
        })}
      </div>
      <div className="divide-y divide-border rounded-lg border border-border">
        {(data?.entries || []).length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">No clock entries this week.</p>}
        {(data?.entries || []).map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`hr-clock-entry-${e.id}`}>
            <div className="min-w-0">
              <p className="font-medium">{ukDate(e.date)} <span className="text-muted-foreground font-normal">· {ukTime(e.clock_in)} → {e.clock_out ? ukTime(e.clock_out) : "now"}</span></p>
              {(e.note || e.edited_by) && <p className="text-xs text-muted-foreground truncate">{e.note}{e.edited_by ? ` (corrected by ${e.edited_by})` : ""}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`tabular-nums text-sm ${e.clock_out ? "" : "text-emerald-700 font-medium"}`}>{hm(e.minutes)}</span>
              {canEdit && (
                <>
                  <button className="p-1 text-muted-foreground hover:text-foreground" onClick={() => setEdit({ ...e, clock_in: toLocalInput(e.clock_in), clock_out: toLocalInput(e.clock_out) })} data-testid={`hr-clock-edit-${e.id}`}><PencilSimple size={14} /></button>
                  <button className="p-1 text-muted-foreground hover:text-destructive" onClick={() => remove(e.id)} data-testid={`hr-clock-delete-${e.id}`}><Trash size={14} /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Correct clock entry</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Clock in</Label><Input type="datetime-local" value={edit.clock_in} onChange={(e) => setEdit({ ...edit, clock_in: e.target.value })} data-testid="hr-clock-edit-in" /></div>
              <div><Label>Clock out</Label><Input type="datetime-local" value={edit.clock_out} onChange={(e) => setEdit({ ...edit, clock_out: e.target.value })} data-testid="hr-clock-edit-out" /></div>
              <div><Label>Note</Label><Input value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} placeholder="Why it was corrected" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} data-testid="hr-clock-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
